// src/components/staff/DriverStatusManager.jsx
import React, { useState } from 'react';
import { Car, AlertTriangle } from 'lucide-react';
import { useStudentContext } from '../../contexts/StudentContext';
import { updateDriverAssignment } from '../../services/studentService';
import { DRIVERS } from '../../utils/constants';
import { styles } from '../../styles/theme';
import { Card } from '../shared/Card';
import { ArrowLeft } from 'lucide-react';

  const DriverStatusManager = ({ onBack }) => {
  const { getStudentsByDriver } = useStudentContext();
  const [driverStatuses, setDriverStatuses] = useState({
    driver1: 'active',
    driver2: 'active'
  });
  const [reassigning, setReassigning] = useState(false);

  const handleDriverStatusChange = (driverId, newStatus) => {
    setDriverStatuses({
      ...driverStatuses,
      [driverId]: newStatus
    });

    if (newStatus === 'absent') {
      const confirmReassign = window.confirm(
        `${DRIVERS.find(d => d.id === driverId).name} is marked absent. Do you want to reassign their students to another driver?`
      );
      
      if (confirmReassign) {
        handleBulkReassign(driverId);
      }
    }
  };

  const handleBulkReassign = async (fromDriverId) => {
    const students = getStudentsByDriver(fromDriverId);
    const availableDrivers = DRIVERS.filter(d => 
      d.id !== fromDriverId && driverStatuses[d.id] === 'active'
    );

    if (availableDrivers.length === 0) {
      alert('No available drivers to reassign to!');
      return;
    }

    const toDriverId = availableDrivers[0].id;
    setReassigning(true);

    try {
      for (const student of students) {
        await updateDriverAssignment(student.id, toDriverId);
      }
      alert(`Successfully reassigned ${students.length} students from ${DRIVERS.find(d => d.id === fromDriverId).name} to ${DRIVERS.find(d => d.id === toDriverId).name}`);
    } catch (error) {
      alert('Failed to reassign some students');
    } finally {
      setReassigning(false);
    }
  };
  
  const handleManualReassign = async (studentId, fromDriverId, toDriverId) => {
    setReassigning(true);
    try {
      await updateDriverAssignment(studentId, toDriverId);
      alert('Student reassigned successfully!');
    } catch (error) {
      alert('Failed to reassign student');
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div>

      <button onClick={onBack} style={styles.secondaryButton}>
  <ArrowLeft size={16} />
  Back to Dashboard
</button>
      <Card title="Driver Status Management">
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Mark drivers as absent and reassign their students for today's pickups
          </p>
        </div>

        {/* Driver Status Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {DRIVERS.map(driver => {
            const students = getStudentsByDriver(driver.id);
            const status = driverStatuses[driver.id];
            
            return (
              <div key={driver.id} style={{
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '1.5rem',
                backgroundColor: status === 'absent' ? '#fee2e2' : '#f9fafb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: status === 'absent' ? '#dc2626' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {status === 'absent' ? (
                      <AlertTriangle style={{ width: '24px', height: '24px', color: 'white' }} />
                    ) : (
                      <Car style={{ width: '24px', height: '24px', color: 'white' }} />
                    )}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 'bold' }}>{driver.name}</h3>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>{driver.phone}</p>
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: status === 'absent' ? '#dc2626' : '#3b82f6' }}>
                    {students.length}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Students Today</div>
                </div>

                <select
                  value={status}
                  onChange={(e) => handleDriverStatusChange(driver.id, e.target.value)}
                  style={{
                    ...styles.select,
                    marginBottom: 0,
                    borderColor: status === 'absent' ? '#dc2626' : '#d1d5db'
                  }}
                  disabled={reassigning}
                >
                  <option value="active">✅ Active</option>
                  <option value="absent">❌ Absent Today</option>
                  <option value="late">⏰ Running Late</option>
                </select>

                {status === 'absent' && students.length > 0 && (
                  <button
                    style={{
                      ...styles.button,
                      width: '100%',
                      marginTop: '0.5rem',
                      backgroundColor: '#f59e0b'
                    }}
                    onClick={() => handleBulkReassign(driver.id)}
                    disabled={reassigning}
                  >
                    Reassign {students.length} Students
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Individual Student Reassignment */}
        <h3 style={{ marginBottom: '1rem' }}>Manual Student Reassignment</h3>
        {DRIVERS.map(driver => {
          const students = getStudentsByDriver(driver.id);
          if (students.length === 0) return null;

          return (
            <div key={driver.id} style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#6b7280' }}>{driver.name}'s Students:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {students.map(student => (
                  <div key={student.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div>
                      <span style={{ fontWeight: '500' }}>{student.firstName} {student.lastName}</span>
                      <span style={{ color: '#6b7280', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                        (Grade {student.grade})
                      </span>
                    </div>
                    <select
                      value={driver.id}
                      onChange={(e) => handleManualReassign(student.id, driver.id, e.target.value)}
                      disabled={reassigning}
                      style={{
                        ...styles.select,
                        marginBottom: 0,
                        width: '150px',
                        padding: '0.5rem'
                      }}
                    >
                      {DRIVERS.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
};

export default DriverStatusManager;